import { ArrowLeft } from '@untitled-ui/icons-react/build/cjs';
import React, { useEffect, useState, useRef, ClipboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import ButtonCmp from 'src/components/ButtonCmp';
import ErrorMsg from 'src/components/errorMsg';
import InputText from 'src/components/InputText/InputText';
import { ROUTES } from 'src/constants/routes';
import { setIsAuthenticated } from 'src/redux/auth.slice';
import { sendOtp, verifyOtp } from 'src/services/AuthService';

import AuthWrapper from '../AppLayout/AuthWrapper';

interface VerifyOtpProps {
    email: string;
    redirectUrl?: string;
    login?: boolean;
    setShowVerification?: (show: boolean) => void;
}

const VerifyOtp: React.FC<VerifyOtpProps> = ({ email, login = false, setShowVerification }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [otp, setOtp] = useState<string[]>(new Array(4).fill(''));
    const [focusOtp, setFocusOtp] = useState<boolean[]>(new Array(4).fill(false));
    const [isValidOtp, setIsValidOtp] = useState<boolean>(false);
    const [wrongOtp, setWrongOtp] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isResendMailLoading, setIsResendMailLoading] = useState<boolean>(false);
    const [errorMsg, setErrorMsg] = useState<string>('');
    const [resendTimer, setResendTimer] = useState<number>(60);

    const inputRefs = useRef<HTMLInputElement[]>([]);

    useEffect(() => {
        if (resendTimer <= 0) return;
        const interval = setInterval(() => setResendTimer((prev) => prev - 1), 1000);

        return () => clearInterval(interval);
    }, [resendTimer]);

    useEffect(() => {
        const otpString = otp.join('');
        setIsValidOtp(otpString.length === 4);
    }, [otp]);

    useEffect(() => {
        const focusFirstInput = () => {
            const firstInput = document.getElementById('otp_text_0') as HTMLInputElement;

            if (firstInput) {
                firstInput.focus();

                return true;
            }

            return false;
        };

        // Try immediate focus
        if (!focusFirstInput()) {
            // If not ready, use requestAnimationFrame for next frame
            requestAnimationFrame(() => {
                if (!focusFirstInput()) {
                    // Last resort: small delay to ensure DOM is fully rendered
                    setTimeout(focusFirstInput, 100);
                }
            });
        }
    }, []);

    const focusInput = (index: number) => {
        if (inputRefs.current[index]) {
            inputRefs.current[index]?.focus();
        }
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').trim();
        const pastedNumbers = pastedData.replace(/\D/g, '').slice(0, 4).split('');
        setErrorMsg('');
        setWrongOtp(false);

        if (pastedNumbers.length) {
            const newOtp = [...otp];
            pastedNumbers.forEach((num, idx) => {
                if (idx < 4) newOtp[idx] = num;
            });
            setOtp(newOtp);

            // Focus last filled input or next empty input
            const focusIndex = Math.min(pastedNumbers.length, 3);
            focusInput(focusIndex);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const value = e.target.value;

        // If backspace or delete, clear current input
        if (!value) {
            const newOtp = [...otp];
            newOtp[index] = '';
            setOtp(newOtp);

            return;
        }

        // Get the last digit
        const lastChar = value[value.length - 1];

        // Only proceed if it's a number
        if (!/^\d$/.test(lastChar)) return;

        // Update the OTP array
        const newOtp = [...otp];
        newOtp[index] = lastChar;
        setOtp(newOtp);
        setErrorMsg('');
        setWrongOtp(false);

        // Move to next input if not at last input
        if (index < 3) {
            const nextInput = document.getElementById(`otp_text_${index + 1}`);

            if (nextInput) {
                nextInput.focus();
            }
        }
    };

    const onVerifyOtp = () => {
        const otpCode = otp.join('');
        if (!isValidOtp) return;

        setIsLoading(true);
        const verificationToken = localStorage.getItem('code');

        verifyOtp({
            otp: otpCode,
            token: verificationToken,
            login: login,
        })
            .then((response) => {
                setIsLoading(false);
                localStorage.setItem('verify_code', response.code);
                localStorage.removeItem('code');

                if (response?.token) {
                    if (login) {
                        localStorage.setItem('token', response.token);
                        dispatch(setIsAuthenticated(true));

                        if (!response?.data?.isSetBasicDetail) {
                            navigate(ROUTES.SETUP_ACCOUNT);
                        } else {
                            navigate(ROUTES.HOME);
                        }
                    }
                } else if (response?.code && response?.data?.isSetPassword === false && login) {
                    navigate(`${ROUTES.CREATE_PASSWORD}`);
                } else if (response?.code && !login) {
                    navigate(`${ROUTES.SET_PASSWORD}`);
                } else {
                    setOtp(new Array(4).fill(''));
                    setErrorMsg(response?.message || t('Auth.Error.Verify_OTP_Failed'));
                    // Focus first input after error
                    const firstInput = document.getElementById('otp_text_0');

                    if (firstInput) {
                        firstInput.focus();
                    }
                }
            })
            .catch((error) => {
                setOtp(new Array(4).fill(''));
                setIsLoading(false);
                setWrongOtp(true);
                setErrorMsg(error?.response?.data?.message || t('Auth.Error.Verify_OTP_Failed'));
                // Focus first input after error
                const firstInput = document.getElementById('otp_text_0');

                if (firstInput) {
                    firstInput.focus();
                }
            });
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        const target = e.target as HTMLInputElement;

        if (e.key === 'Enter') {
            e.preventDefault();

            if (isValidOtp && !isLoading) {
                onVerifyOtp();
            }

            return;
        }

        if (e.key === 'Backspace') {
            e.preventDefault();

            if (target.value) {
                const newOtp = [...otp];
                newOtp[index] = '';
                setOtp(newOtp);
            } else if (index > 0) {
                const newOtp = [...otp];
                newOtp[index - 1] = '';
                setOtp(newOtp);
                const prevInput = document.getElementById(`otp_text_${index - 1}`);

                if (prevInput) {
                    prevInput.focus();
                }
            }
        } else if (e.key === 'ArrowLeft' && index > 0) {
            e.preventDefault();
            const prevInput = document.getElementById(`otp_text_${index - 1}`);

            if (prevInput) {
                prevInput.focus();
            }
        } else if (e.key === 'ArrowRight' && index < 3) {
            e.preventDefault();
            const nextInput = document.getElementById(`otp_text_${index + 1}`);

            if (nextInput) {
                nextInput.focus();
            }
        }
    };

    const formatTimer = (seconds: number) => {
        const m = String(Math.floor(seconds / 60)).padStart(2, '0');
        const s = String(seconds % 60).padStart(2, '0');

        return `${m}:${s}`;
    };

    const sendVerifyOtpMail = () => {
        if (isResendMailLoading || resendTimer > 0) return;

        setIsResendMailLoading(true);
        sendOtp({ email })
            .then((response) => {
                setIsResendMailLoading(false);
                setResendTimer(60);

                if (!response?.token) {
                    setErrorMsg(response?.message || t('Auth.Error.Send_OTP_Failed'));
                }
            })
            .catch((error) => {
                setIsResendMailLoading(false);
                setErrorMsg(error?.response?.data?.message || t('Auth.Error.Send_OTP_Failed'));
            });
    };

    return (
        <AuthWrapper
            authHeading={t('Auth.Verify_Email.Check_Email')}
            authSubTitle={
                <div className="text-left flex flex-wrap gap-x-1.5">
                    <span className="text-grayLight600 text-[14px] leading-[20px] whitespace-nowrap font-normal">{t('Auth.Verify_Email.Verification_Code_Sent')}</span>{' '}
                    <span className="text-grayLight600 text-[14px] leading-[20px] truncate whitespace-nowrap font-semibold">{email}</span>
                </div>
            }
        >
            <>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();

                        if (isValidOtp && !isLoading) {
                            onVerifyOtp();
                        }
                    }}
                >
                    <div className="flex justify-center gap-2" onPaste={handlePaste}>
                        {otp.map((digit, index) => {
                            const inputProps = {
                                key: `otp_text_${index}`,
                                id: `otp_text_${index}`,
                                inputName: 'otp',
                                inputType: 'text' as const,
                                isError: wrongOtp,
                                isAllowZeroValue: true,
                                maxLength: 1,
                                className: `otp-${index} w-[48px] h-[48px] bg-white min-w-[48px] min-h-[48px] text-center !p-1 leading-[38px] text-[30px] placeholder:!text-grayLight600 placeholder-shown:border-borderPrimary focus:border-primary border-borderPrimary text-grayLight600 hover:border-primary [&:focus:not(:placeholder-shown)]:border-borderPrimary shadow-[0px_1px_2px_0px_#1018280D]
 ${wrongOtp ? ' !border-fgErrorPrimary !text-fgErrorPrimary ' : ' '}`,
                                value: digit,
                                onFocus: () => {
                                    const newFocusOtp = [...focusOtp];
                                    newFocusOtp[index] = true;
                                    setFocusOtp(newFocusOtp);
                                },
                                onBlur: () => {
                                    const newFocusOtp = [...focusOtp];
                                    newFocusOtp[index] = false;
                                    setFocusOtp(newFocusOtp);
                                },
                                onChangeFunc: (e: any) => handleInputChange(e, index),
                                handleKeyDown: (e: any) => handleKeyDown(e, index),
                                onKeyPress: (e: any) => {
                                    // Only allow numeric input
                                    if (!/[0-9]/.test(e.key)) {
                                        e.preventDefault();
                                    }
                                },
                            };

                            return <InputText {...inputProps} key={inputProps.key} />;
                        })}
                    </div>

                    <ErrorMsg errorText={errorMsg} mainClassName="text-center" />

                    <ButtonCmp type="submit" size="lg" className="btn_primary_black w-full mt-5" onClick={onVerifyOtp} loading={isLoading} disabled={!isValidOtp || isLoading}>
                        {t('Auth.Continue')}
                    </ButtonCmp>
                </form>

                <div className="text-utilityTertiary600 text-sm font-normal flex flex-row items-center justify-center gap-x-1 text-center mt-6">
                    <span className="text-utilityTertiary600 text-[12px] leading-[18px] font-semibold">{t('Auth.Verify_Email.No_Email')}</span>
                    {isResendMailLoading ? (
                        <span className="loading loading-spinner ml-2 h-3 w-3 text-[12px] leading-[18px] font-semibold"></span>
                    ) : (
                        <>
                            <a
                                className={`text-[12px] leading-[18px] font-semibold ${resendTimer > 0 ? 'opacity-50 text-primary cursor-default pointer-events-none' : 'text-primary cursor-pointer'}`}
                                onClick={sendVerifyOtpMail}
                            >
                                {t('Auth.Verify_Email.Click_Resend')}
                            </a>
                            {resendTimer > 0 && <span className="text-gray500 text-[12px] leading-[18px] font-semibold">({formatTimer(resendTimer)})</span>}
                        </>
                    )}
                </div>
                <div className="text-center mt-6">
                    <Link to="/login" onClick={() => setShowVerification?.(false)} className="text-grayLight600 gap-x-1.5 cursor-pointer inline-flex items-center justify-center">
                        <ArrowLeft className="w-4 h-4 object-contain text-grayLight600" />
                        <span className="text-grayLight600 text-[12px] leading-[18px] font-semibold">{t('Auth.Verify_Email.Back_To_Login')}</span>
                    </Link>
                </div>
            </>
        </AuthWrapper>
    );
};

export default VerifyOtp;
